import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const Mortgage = () => {
  const [loanAmount, setLoanAmount] = useState("");
  const [interestRate, setInterestRate] = useState("");
  const [loanTerm, setLoanTerm] = useState("");
  const [propertyTaxes, setPropertyTaxes] = useState("");
  const [insurance, setInsurance] = useState("");
  const [monthlyPayment, setMonthlyPayment] = useState(0);

  useEffect(() => {
    calculateMortgage();

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [loanAmount, interestRate, loanTerm, propertyTaxes, insurance]);

  const calculateMortgage = () => {
    // Convert inputs to numbers
    const P = parseFloat(loanAmount) || 0; // Loan Amount
    const r = parseFloat(interestRate) / 100 / 12 || 0; // Monthly Interest Rate
    const n = parseFloat(loanTerm) * 12 || 0; // Loan Term in Months
    const taxes = parseFloat(propertyTaxes) || 0; // Property Taxes
    const insuranceAmount = parseFloat(insurance) || 0; // Insurance

    // If loanAmount and loanTerm are provided, proceed with the calculation
    if (P > 0 && n > 0) {
      // Mortgage Formula: M = P[r(1 + r)^n] / [(1 + r)^n – 1]
      let monthlyPayment =
        (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);

      // If no interest rate, just divide loan amount by months
      if (r === 0) {
        monthlyPayment = P / n;
      }

      // Add property taxes and insurance to monthly payment (if any)
      monthlyPayment += (taxes + insuranceAmount) / 12;

      setMonthlyPayment(monthlyPayment.toFixed(2)); // Set the result
    } else {
      setMonthlyPayment(0); // Set to 0 if required values are missing
    }
  };

  return (
    <Layout title="Mortgage Calculator" path="mortgage">
      <section
        className="topics-detail-section section-padding"
        id="topics-detail"
      >
        <div className="container">
          <div className="row">
            {/* Mortgage Calculator Section (75% space) */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-5">Mortgage Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Loan Amount (P) */}
                    <div className="col-md-6 col-lg-4 mb-2">
                      <label htmlFor="loanAmount" className="form-label">
                        Loan Amount (₹)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Loan Amount:</strong> The amount you borrow from the lender. This will be the principal for your mortgage loan."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="loanAmount"
                        step="0.01"
                        placeholder="Enter loan amount"
                        value={loanAmount}
                        onChange={(e) => setLoanAmount(e.target.value)}
                      />
                    </div>

                    {/* Interest Rate */}
                    <div className="col-md-6 col-lg-4 mb-2">
                      <label htmlFor="interestRate" className="form-label">
                        Annual Interest Rate (%)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Interest Rate:</strong> The annual interest rate is the percentage charged by the lender for borrowing the loan amount."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="interestRate"
                        step="0.01"
                        placeholder="Enter interest rate"
                        value={interestRate}
                        onChange={(e) => setInterestRate(e.target.value)}
                      />
                    </div>

                    {/* Loan Term (n in years) */}
                    <div className="col-md-6 col-lg-4 mb-2">
                      <label htmlFor="loanTerm" className="form-label">
                        Loan Term (Years)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Loan Term:</strong> The length of time (in years) over which you will repay the loan. Common terms are 15, 20, or 30 years."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="loanTerm"
                        step="1"
                        placeholder="Enter loan term in years"
                        value={loanTerm}
                        onChange={(e) => setLoanTerm(e.target.value)}
                      />
                    </div>

                    {/* Property Taxes (Optional) */}
                    <div className="col-md-6 col-lg-4 mb-2">
                      <label htmlFor="propertyTaxes" className="form-label">
                        Annual Property Taxes (₹){" "}
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Property Taxes:</strong> These are the annual taxes assessed by the government on your property. This amount is often included in your mortgage payments."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                        {/* <span
                          className="text-muted"
                          style={{ fontSize: "0.9em" }}
                        >
                          (optional)
                        </span> */}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="propertyTaxes"
                        step="0.01"
                        placeholder="Enter annual property taxes (optional)"
                        value={propertyTaxes}
                        onChange={(e) => setPropertyTaxes(e.target.value)}
                      />
                      <span
                          className="text-muted"
                          style={{ fontSize: "0.9em" }}
                        >
                          (optional)
                        </span>
                    </div>

                    {/* Insurance (Optional) */}
                    <div className="col-md-6 col-lg-4 mb-2">
                      <label htmlFor="insurance" className="form-label">
                        Annual Insurance (₹){" "}
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Insurance:</strong> The annual cost of homeowner's insurance. This is typically required by lenders."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                        {/* <span
                          className="text-muted"
                          style={{ fontSize: "0.9em" }}
                        >
                          (optional)
                        </span> */}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="insurance"
                        step="0.01"
                        placeholder="Enter annual insurance (optional)"
                        value={insurance}
                        onChange={(e) => setInsurance(e.target.value)}
                      />
                      <span
                          className="text-muted"
                          style={{ fontSize: "0.9em" }}
                        >
                          (optional)
                        </span>
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Monthly Payment:{" "}
                        <span id="monthlyPayment" className="fw-bold ms-3">
                          ₹{monthlyPayment}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section (25% space) */}
            <Advertise />
          </div>
        </div>
      </section>
      <section className="introduction-section" id="introduction">
            <div className="container">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="p-5">
                            <h3 className="text-center mb-5">Introduction to the Mortgage Calculator</h3>
                
                            
                            <h4 className="mt-3 mb-3">What is a Mortgage Calculator?</h4>
                            <p>A **Mortgage Calculator** is a tool that helps individuals and businesses estimate their monthly mortgage payments based on loan amount, interest rate, and loan term. It simplifies the process of understanding how different loan conditions affect monthly payments, providing a valuable tool for both budgeting and planning home or property purchases.</p>
                
                            
                            <h4 className="mt-4 mb-3">How to Calculate Mortgage Payments?</h4>
                            
                            <p>Where: </p>
                            <ul>
                                <li><strong>P:</strong> Principal loan amount (the amount borrowed).</li>
                                <li><strong>r:</strong> Monthly interest rate (annual interest rate divided by 12).</li>
                                <li><strong>n:</strong> Total number of payments (loan term in months).</li>
                            </ul>
                            <p>To use the calculator:</p>
                            <ul>
                                <li>Enter the loan amount (the principal amount you are borrowing).</li>
                                <li>Provide the annual interest rate and the loan term (in years).</li>
                                <li>Optionally, you can include annual property taxes and insurance fees, if applicable.</li>
                            </ul>
                            <p>The calculator then computes the monthly payment based on these inputs, helping you assess your mortgage affordability.</p>
                
                           
                            <h4 className="mt-4 mb-3">What Factors Affect Mortgage Payments?</h4>
                            <p>The monthly mortgage payment calculation depends on several key factors, including:</p>
                            <ul>
                                <li><strong>Loan Amount (P):</strong> The total amount you borrow from the lender.</li>
                                <li><strong>Interest Rate (r):</strong> The annual interest rate on the mortgage, which impacts the overall cost of the loan.</li>
                                <li><strong>Loan Term (n):</strong> The length of the loan in years, which determines how many payments you make over time.</li>
                                <li><strong>Property Taxes:</strong> Annual taxes that may be included in your mortgage payment, depending on the agreement with your lender.</li>
                                <li><strong>Insurance:</strong> Annual insurance costs that can be added to the mortgage payment.</li>
                            </ul>
                
                            
                            <h4 className="mt-4 mb-3">What are the Benefits of a Mortgage Calculator?</h4>
                            <p>Using a mortgage calculator provides several important benefits:</p>
                            <ul>
                                <li><strong>Accurate Payment Estimates:</strong> Helps you predict your monthly payments and plan your finances accordingly.</li>
                                <li><strong>Loan Affordability:</strong> Assists in determining if the loan fits your budget, helping you avoid financial strain.</li>
                                <li><strong>Time-Saving:</strong> Offers quick, automatic calculations without the need for manual math, saving you time and effort.</li>
                                <li><strong>Improved Financial Planning:</strong> Enables better understanding of loan options and interest rates, leading to smarter decisions.</li>
                            </ul>
                
                            <p>Overall, the Mortgage Calculator is an essential tool for homebuyers, property investors, and anyone looking to secure a loan. It simplifies the process of estimating payments and helps users manage their finances more efficiently.</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </Layout>
  );
};

export default Mortgage;
